/* theme.js — FLOTE redesign theme. Cart (AJAX API + Section Rendering API),
 * drawers, toggles. No framework.
 *
 * Contract for section markup:
 *   <form data-product-form action="/cart/add" method="post">  name="id", name="quantity",
 *        optional name="selling_plan"; submit → added via /cart/add.js, drawer re-rendered and opened.
 *        A submit button with [data-buy-now] adds (unless already in the cart) and goes to /checkout.
 *        A [data-form-error] element inside the form receives error text.
 *   [data-cart-open] / [data-cart-close]   open/close the cart drawer (section "cart-drawer").
 *   [data-line-change][data-line][data-qty] change a cart line's quantity (0 = remove).
 *   [data-cart-count]                       text set to the cart item count after every change.
 *   [data-fl-toggle="#id"]                  toggles [hidden] on #id and aria-expanded on itself.
 *   [data-fl-open="#id"] / [data-fl-close="#id"]  open/close a drawer or modal (adds .fl-lock).
 *   window.FL.cart.add(formData) / change(line, qty) / refresh() for custom section scripts.
 *   Fires `fl:cart:updated` on document with detail = cart JSON.
 */
(() => {
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const SECTION = 'cart-drawer';

  const qs = (s, el = document) => el.querySelector(s);
  const lock = (on) => document.documentElement.classList.toggle('fl-lock', on);

  function drawer() { return qs('[data-cart-drawer]'); }
  function openCart() { const d = drawer(); if (!d) return; d.hidden = false; lock(true); qs('[data-cart-close]', d)?.focus({ preventScroll: true }); }
  function closeCart() { const d = drawer(); if (!d) return; d.hidden = true; lock(false); }

  function renderSections(sections) {
    if (!sections || !sections[SECTION]) return;
    const wasOpen = drawer() && !drawer().hidden;
    const wrap = document.getElementById(`shopify-section-${SECTION}`);
    if (!wrap) return;
    const doc = new DOMParser().parseFromString(sections[SECTION], 'text/html');
    const fresh = doc.getElementById(`shopify-section-${SECTION}`);
    wrap.innerHTML = fresh ? fresh.innerHTML : sections[SECTION];
    if (wasOpen) drawer().hidden = false;
  }

  async function refreshCount() {
    const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    document.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = cart.item_count; });
    document.dispatchEvent(new CustomEvent('fl:cart:updated', { detail: cart }));
    return cart;
  }

  async function post(url, body) {
    const res = await fetch(`${root}${url}`, { method: 'POST', headers: { Accept: 'application/json' }, body });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.status) throw new Error(data.description || data.message || 'Something went wrong. Please try again.');
    return data;
  }

  async function add(formData) {
    formData.append('sections', SECTION);
    formData.append('sections_url', window.location.pathname);
    const data = await post('cart/add.js', formData);
    renderSections(data.sections);
    await refreshCount();
    return data;
  }

  async function change(line, quantity) {
    const fd = new FormData();
    fd.append('line', line); fd.append('quantity', quantity);
    fd.append('sections', SECTION); fd.append('sections_url', window.location.pathname);
    const data = await post('cart/change.js', fd);
    renderSections(data.sections);
    await refreshCount();
    return data;
  }

  async function refresh() {
    const r = await fetch(`${window.location.pathname}?sections=${SECTION}`).then((x) => x.json());
    renderSections(r);
    return refreshCount();
  }

  let toastTimer;
  function toast(text) {
    const t = qs('[data-toast]'); if (!t) return;
    qs('[data-toast-text]', t).textContent = text;
    t.hidden = false; clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { t.hidden = true; }, 3200);
  }

  window.FL = { cart: { add, change, refresh, open: openCart, close: closeCart }, toast };

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-product-form]');
    if (!form) return;
    e.preventDefault();
    const btn = e.submitter || form.querySelector('[type="submit"]');
    const err = form.querySelector('[data-form-error]');
    if (err) { err.hidden = true; err.textContent = ''; }
    btn && btn.setAttribute('aria-busy', 'true');
    if (btn) btn.disabled = true;
    try {
      // Buy now: straight to checkout. Skip the add when the same variant + plan is already
      // in the cart, so an earlier "Add to cart" doesn't turn into a quantity of 2.
      if (btn && btn.hasAttribute('data-buy-now')) {
        const fd = new FormData(form);
        const id = Number(fd.get('id')); const plan = Number(fd.get('selling_plan')) || null;
        const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
        const inCart = cart.items.some((i) => i.variant_id === id && ((i.selling_plan_allocation && i.selling_plan_allocation.selling_plan.id) || null) === plan);
        if (!inCart) await post('cart/add.js', fd);
        window.location.assign(`${root}checkout`);
        return;
      }
      await add(new FormData(form));
      if (form.dataset.openCart === 'false') toast(form.dataset.toast || 'Added to cart');
      else openCart();
    } catch (ex) {
      if (err) { err.textContent = ex.message; err.hidden = false; } else alert(ex.message);
    }
    // Not in a finally: a successful Buy now returns above and stays busy while checkout loads.
    if (btn) { btn.disabled = false; btn.removeAttribute('aria-busy'); }
  });

  document.addEventListener('click', async (e) => {
    const t = e.target;
    const o = t.closest('[data-cart-open]');
    if (o) { e.preventDefault(); const tt = qs('[data-toast]'); if (tt) tt.hidden = true; openCart(); return; }
    if (t.closest('[data-cart-close]')) { e.preventDefault(); closeCart(); return; }
    const lc = t.closest('[data-line-change]');
    if (lc) {
      e.preventDefault(); lc.disabled = true;
      try { await change(Number(lc.dataset.line), Number(lc.dataset.qty)); } catch (ex) { alert(ex.message); }
      return;
    }
    const tg = t.closest('[data-fl-toggle]');
    if (tg) {
      const target = qs(tg.dataset.flToggle); if (!target) return;
      e.preventDefault();
      target.hidden = !target.hidden;
      tg.setAttribute('aria-expanded', String(!target.hidden));
      return;
    }
    const op = t.closest('[data-fl-open]');
    if (op) { const m = qs(op.dataset.flOpen); if (m) { e.preventDefault(); m.hidden = false; lock(true); } return; }
    const cl = t.closest('[data-fl-close]');
    if (cl) { const m = qs(cl.dataset.flClose); if (m) { e.preventDefault(); m.hidden = true; lock(false); } }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    closeCart();
    document.querySelectorAll('[data-fl-modal]:not([hidden])').forEach((m) => { m.hidden = true; });
    lock(false);
  });

  // Back/forward cache can show a stale count.
  window.addEventListener('pageshow', (e) => { if (e.persisted) refresh(); });
})();

// Mobile chat bubble: reveal after the first scroll past ~half a screen.
(() => {
  const ready = () => {
    if (window.scrollY > window.innerHeight * 0.5) {
      document.body.classList.add('fl-chat-ready');
      window.removeEventListener('scroll', ready);
    }
  };
  window.addEventListener('scroll', ready, { passive: true });
  ready();
})();

// Chat bubble steps aside while the product buy box is on screen, so it never
// covers a supply option or price (review 2026-10-20: it hid the 6-month row).
(() => {
  const box = document.querySelector('[data-pd-form]');
  if (!box || !('IntersectionObserver' in window)) return;
  new IntersectionObserver(([e]) => {
    document.body.classList.toggle('fl-chat-aside', e.isIntersecting);
  }, { threshold: 0.15 }).observe(box);
})();

// FAQ deep links (e.g. #money-back-guarantee from the guarantee banner and the
// product buy box): when the hash names a <details> question, open it, clear a
// category filter that hides it, and scroll it into view.
(() => {
  const openFromHash = (hash) => {
    if (!hash || hash.length < 2) return false;
    let el = null;
    try { el = document.getElementById(decodeURIComponent(hash.slice(1))); } catch (e) { return false; }
    if (!el || el.tagName !== 'DETAILS') return false;
    if (el.hidden) {
      const all = el.closest('section')?.querySelector('[data-cat="All"], [data-faq-cat="All"]');
      if (all) all.click();
    }
    el.open = true;
    el.scrollIntoView({ block: 'start', behavior: 'smooth' });
    return true;
  };
  document.addEventListener('click', (e) => {
    const a = e.target.closest('a[href*="#"]');
    if (!a || a.pathname !== location.pathname || a.host !== location.host) return;
    if (openFromHash(a.hash)) { e.preventDefault(); history.replaceState(null, '', a.hash); }
  });
  window.addEventListener('hashchange', () => openFromHash(location.hash));
  if (location.hash) openFromHash(location.hash);
})();
